// Supabase Logs Viewer Component
// Displays Edge Function, Postgres, and Auth logs with filtering

import { useState, useEffect, useCallback } from 'react';
import { Terminal, RefreshCw, AlertCircle, AlertTriangle, Info, Check, Clock } from 'lucide-react';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PxMark } from '@/components/PxMark';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface LogEntry {
    id: string;
    timestamp: string;
    level: 'info' | 'warn' | 'error' | 'debug';
    message: string;
    metadata?: Record<string, any>;
}

type LogSource = 'edge-functions' | 'postgres' | 'auth';

interface SupabaseLogsViewerProps {
    pixelProjectId?: string;
}

export function SupabaseLogsViewer({ pixelProjectId }: SupabaseLogsViewerProps) {
    const { getLogs, isConnected } = useSupabaseMCP(pixelProjectId);
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [source, setSource] = useState<LogSource>('edge-functions');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(false);

    const loadLogs = useCallback(async () => {
        if (!isConnected) return;

        setIsLoading(true);
        setError(null);

        try {
            const result = await getLogs(source, 100);
            if (result.success && result.data) {
                // Normalize log data
                const logsData = Array.isArray(result.data) ? result.data : [];
                const normalizedLogs: LogEntry[] = logsData.map((log: any, index: number) => ({
                    id: log.id || `log-${index}`,
                    timestamp: log.timestamp || log.created_at || new Date().toISOString(),
                    level: log.level || log.severity || 'info',
                    message: log.message || log.event_message || JSON.stringify(log),
                    metadata: log.metadata || log.event_metadata,
                }));
                setLogs(normalizedLogs);
            } else {
                setError(result.error || 'Failed to load logs');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [isConnected, getLogs, source]);

    useEffect(() => {
        if (isConnected) {
            loadLogs();
        }
    }, [isConnected, source, loadLogs]);

    // Auto-refresh every 10 seconds if enabled
    useEffect(() => {
        if (!autoRefresh || !isConnected) return;

        const interval = setInterval(loadLogs, 10000);
        return () => clearInterval(interval);
    }, [autoRefresh, isConnected, loadLogs]);

    const getLevelIcon = (level: string) => {
        switch (level.toLowerCase()) {
            case 'error':
                return <AlertCircle className="h-4 w-4 text-err" />;
            case 'warn':
            case 'warning':
                return <AlertTriangle className="h-4 w-4 text-warn" />;
            case 'debug':
                return <Terminal className="h-4 w-4 text-muted-foreground" />;
            case 'success':
                return <Check className="h-4 w-4 text-ok" />;
            default:
                return <Info className="h-4 w-4 text-info" />;
        }
    };

    const getLevelColor = (level: string) => {
        switch (level.toLowerCase()) {
            case 'error':
                return 'text-err';
            case 'warn':
            case 'warning':
                return 'text-warn';
            case 'debug':
                return 'text-muted-foreground';
            case 'success':
                return 'text-ok';
            default:
                return 'text-foreground';
        }
    };

    const formatTimestamp = (timestamp: string) => {
        try {
            return new Date(timestamp).toLocaleString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                day: '2-digit',
                month: 'short',
            });
        } catch {
            return timestamp;
        }
    };

    if (!isConnected) {
        return (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
                <PxMark state="idle" size={24} className="mb-4" />
                <h3 className="mb-2 text-[16px] font-medium text-foreground">Supabase non connecté</h3>
                <p className="text-sm text-muted-foreground">
                    Connectez votre compte Supabase via le bouton dans la sidebar.
                </p>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border p-4">
                <div className="flex items-center gap-2">
                    <Terminal className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-[13px] font-medium text-foreground">Logs</h2>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={source} onValueChange={(v) => setSource(v as LogSource)}>
                        <SelectTrigger className="w-40 h-8 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="edge-functions">Edge Functions</SelectItem>
                            <SelectItem value="postgres">Postgres</SelectItem>
                            <SelectItem value="auth">Auth</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        variant={autoRefresh ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className="h-8 gap-1.5 text-xs"
                    >
                        <Clock className="h-3.5 w-3.5" />
                        Auto
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadLogs}
                        disabled={isLoading}
                        className="h-8 gap-1.5"
                    >
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
                {isLoading && logs.length === 0 ? (
                    <div className="flex items-center justify-center p-8">
                        <PxMark state="thinking" size={20} />
                    </div>
                ) : error ? (
                    <div className="m-4 border-l-2 border-err bg-err/5 p-3 font-mono text-[12px] text-foreground">{error}</div>
                ) : logs.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                        Aucun log trouvé pour cette source.
                    </div>
                ) : (
                    <div className="font-mono text-xs">
                        {logs.map((log) => (
                            <div
                                key={log.id}
                                className="flex gap-3 border-b border-border px-4 py-2 hover:bg-secondary"
                            >
                                <span className="shrink-0 whitespace-nowrap text-faint">
                                    {formatTimestamp(log.timestamp)}
                                </span>
                                <span className="shrink-0">{getLevelIcon(log.level)}</span>
                                <span className={cn("flex-1 break-all", getLevelColor(log.level))}>
                                    {log.message}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}

export default SupabaseLogsViewer;
